import { SearchOutlined, SyncOutlined } from '@ant-design/icons';
import {
	Button,
	Col,
	Collapse,
	ConfigProvider,
	DatePicker,
	Form,
	Grid,
	Input,
	Row,
	Select,
	TimeRangePickerProps,
} from 'antd';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../../../assets/api/endpoints';
import { Status } from '../types/Status';
import { RequetStatus } from './RequetStatus';

import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

type WalletFiltersProps = {
	className?: string;
	numberOfEntries: string;
	setNumberOfEntries: (numberOfEntries: string) => void;
	search: string;
	setDateRange: (range: string[]) => void;
	setSearch: (search: string) => void;
	status: string | null;
	setStatus: (status: string | null) => void;
	refresh: () => void;
	setAccount: (account: string) => void;
	account: string;
	assignee: any;
	setAssignee: (vaule: any) => void;
};

export const RequestFilters = (props: WalletFiltersProps) => {
	const { t } = useTranslation();
	const { lg } = Grid.useBreakpoint();
	const [form] = Form.useForm();
	const [isLoading, setIsLoading] = useState(false);
	const [transactionStatusList, setTransactionStatusList] = useState<any>([]);
	const [assignees, setAssignees] = useState<any>([]);
	const [accounts, setAccounts] = useState<any>([]);
	const [transactionStatusListResult, setTransactionStatusListResult] = useState<any>([]);
	const [assigneesResult, setAssigneesResult] = useState<any>([]);
	const [accountsResult, setAccountsResult] = useState<any>([]);
	const [activeKey, setActiveKey] = useState<string[] | string>(lg ? ['1'] : []);
	useEffect(() => {
		setIsLoading(true);
		Promise.all([
			axios.get(`${API_ENDPOINTS.withdrawRequest}/status`),
			axios.get(`${API_ENDPOINTS.adminList}`),
			axios.get(`${API_ENDPOINTS.accounts}`),
		])
			.then(([statuses, assignees, accounts]) => [statuses?.data, assignees?.data, accounts.data])
			.then(([statuses, assignees, accounts]) => {
				setTransactionStatusListResult(statuses);
				setAssigneesResult(assignees);
				setAccountsResult(accounts);
			})
			.finally(() => setIsLoading(false));
	}, []);

	useEffect(() => {
		const requestStatusList = [
			{
				label: <>{t('all')}</>,
				value: '',
			},
		].concat(
			transactionStatusListResult.map((status: any) => ({
				label: <RequetStatus status={status as Status} />,
				value: status,
			})),
		);
		const AssigneesRequestList = [
			{
				label: <>{t('all')}</>,
				value: '',
			},
		].concat(
			assigneesResult.map((assinee: any) => ({
				label: assinee.fullName,
				value: assinee.id,
			})),
		);

		const requestAccountsList = [
			{
				label: <>{t('all')}</>,
				value: '',
			},
		].concat(
			accountsResult.map((account: any) => ({
				label: (
					<>
						{account.accountGroup} | {account.number} | {account.tradingPlatform}
					</>
				),
				value: account.number,
			})),
		);
		setTransactionStatusList(requestStatusList);
		setAssignees(AssigneesRequestList);
		setAccounts(requestAccountsList);
	}, [t, accountsResult, assigneesResult, transactionStatusListResult]);
	useEffect(() => {
		// Open panel by default if large screen (`lg`) is active, otherwise close
		if (lg) {
			setActiveKey(['1']);
		} else {
			setActiveKey([]);
		}
	}, [lg]);
	const onDateRangeChange = useCallback((values: TimeRangePickerProps['value']) => {
		const range = (values || [])?.map((value) => dayjs(value).format('YYYY-MM-DD 00:00:00'));
		props.setDateRange(range);
	}, []);
	return (
		<ConfigProvider
			theme={{
				inherit: true,
				components: {
					TreeSelect: {
						nodeSelectedBg: '#fff',
						nodeHoverBg: 'red',
					},
				},
			}}
		>
			<div className={props.className}>
				<Collapse activeKey={activeKey} onChange={(key) => setActiveKey(key)}>
					<Collapse.Panel header={t('Filter Options')} key='1'>
						<div className='border-[#000d1d]/10 rounded-md shadow-md py-3 pb-0 px-4 border border-solid border-gray-200'>
							<Form form={form} layout='vertical'>
								<Row gutter={[16, 0]}>
									<Col xs={24} lg={6} className='w-full'>
										<Form.Item label={t('dateRange')} name='dateRange'>
											<RangePicker onChange={(values) => onDateRangeChange(values)} className='w-full' />
										</Form.Item>
									</Col>
									<Col xs={24} lg={6} className='w-full'>
										<Form.Item label={t('status')} name='status' className='min-w-64'>
											<Select
												loading={isLoading}
												value={props.status}
												placeholder={t('all')}
												onChange={(e) => props.setStatus(e)}
												options={transactionStatusList}
												className='w-full'
											/>
										</Form.Item>
									</Col>
									<Col xs={24} lg={6} className='w-full'>
										<Form.Item label={t('account')} name='account' className='min-w-64'>
											<Select
												loading={isLoading}
												value={props.account}
												placeholder={t('all')}
												onChange={(e) => props.setAccount(e)}
												options={accounts}
												showSearch
												filterOption={(inputValue, option: any) =>
													option?.label?.props?.children
														? option?.label?.props?.children?.toString().includes(inputValue.toLowerCase())
														: false
												}
												className='w-full'
											/>
										</Form.Item>
									</Col>
									<Col xs={24} lg={6} className='w-full'>
										<Form.Item label={t('assignee')} name='assignee' className='min-w-64'>
											<Select
												loading={isLoading}
												value={props.assignee}
												placeholder={t('all')}
												onChange={(e) => props.setAssignee(e)}
												options={assignees}
												className='w-full'
											/>
										</Form.Item>
									</Col>
								</Row>
							</Form>
						</div>
					</Collapse.Panel>
				</Collapse>
				<Row gutter={[16, 16]} align='middle' className='my-2'>
					<Col xs={24} lg={24} className='flex gap-2 items-center justify-end'>
						<Button
							onClick={props.refresh}
							className='bg-[#EAECEF] text-[#1E2329] border-none shadow-none'
							htmlType='button'
							type='primary'
							icon={<SyncOutlined />}
						></Button>
						<Input
							value={props.search}
							onChange={(e) => props.setSearch(e.target.value)}
							className='xss:w-36 h-9 md:w-64 border-none'
							placeholder={t<string>('searchWallet')}
							prefix={<SearchOutlined />}
						/>
					</Col>
				</Row>
			</div>
		</ConfigProvider>
	);
};
